/**
 * Open the EXISTING conflict check for a pipeline row.
 *
 * BM-H3263 (Client Care, 29 Sep 2026): on a matter awaiting conflict
 * clearance with no matter ID yet, "Open Conflict Check" and "Open Full
 * Matter" both went to /conflict-check?matter=<ref>. App.js redirects that
 * to /conflict-search, which is a blank new search. The check that had
 * already been run, and the approval waiting on it, could not be reached.
 *
 * Every conflict-stage pipeline row carries conflict_check_id, including
 * rows with no matter reference, so the check is looked up by id:
 *
 *   - the director the approval was issued to goes to the approval page,
 *     /conflict-approval/{token};
 *   - everyone else goes to the check's own page, /conflict-check/{id},
 *     which shows who the approval went to and its status. The approval
 *     page refuses anyone but the addressed director (BM-H267) and audits
 *     the refusal, so sending other staff there would only show them an
 *     error.
 *
 * A new conflict search is opened only when the row has no conflict check
 * at all, which is the one case where starting a check is the right action.
 */

// Same sources, same order, as hub/conflict_approval.py token_issued_to().
const RECIPIENT_FIELDS = [
  "approval_token_issued_to",
  "approval_recipient_email",
  "approval_supervisor_email",
];

const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");

export function approvalRecipient(cc) {
  for (const field of RECIPIENT_FIELDS) {
    const value = norm(cc?.[field]);
    if (value) return value;
  }
  return "";
}

export function conflictCheckPath(cc, userEmail) {
  const token = cc?.approval_token;
  const me = norm(userEmail);
  if (token && me && approvalRecipient(cc) === me) {
    return `/conflict-approval/${encodeURIComponent(token)}`;
  }
  return `/conflict-check/${encodeURIComponent(cc.id)}`;
}

// Same lookup PipelineTrackerCore uses for the signed-in user.
export function storedUserEmail() {
  try {
    const raw = localStorage.getItem("user");
    const u = raw ? JSON.parse(raw) : null;
    return norm(
      u?.user_email || u?.email || u?.ms_email ||
      sessionStorage.getItem("bonsai_user_email") ||
      localStorage.getItem("bonsai_user_email") || ""
    );
  } catch {
    return "";
  }
}

export async function openConflictCheck({
  conflictCheckId, matterRef, userEmail, axios, apiBase, navigate, onError,
}) {
  const handleError = onError || ((msg) => {
    // eslint-disable-next-line no-console
    console.error("[openConflictCheck]", msg);
    if (typeof window !== "undefined" && typeof window.alert === "function") {
      window.alert(msg);
    }
  });

  const id = (conflictCheckId || "").trim();
  const ref = (matterRef || "").trim();

  if (!id) {
    if (ref) {
      navigate(`/conflict-check?matter=${encodeURIComponent(ref)}`);
      return;
    }
    handleError("Cannot open the conflict check: this row has no conflict check or matter reference.");
    return;
  }

  let cc;
  try {
    const res = await axios.get(`${apiBase}/conflict-checks/${encodeURIComponent(id)}`);
    cc = res?.data;
  } catch (err) {
    // The check's own page makes the same request and shows the error to
    // the user, so open it rather than fail here in silence.
    // eslint-disable-next-line no-console
    console.warn("[openConflictCheck] lookup failed, opening the check page", err);
  }

  const email = userEmail !== undefined ? userEmail : storedUserEmail();
  navigate(conflictCheckPath({ ...(cc || {}), id }, email));
}
